import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, DelButton, ActiveButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import {
  getSKUPackTypeList2,
  getSKUPackTypeStatusCount,
  deactivateSKUPackTypeById,
  activeSKUPackType,
} from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";

function SKUPackTypeList() {
  const navigate = useNavigate();
  const {permissions, allowAll} = useTeamValue();
  const location = useLocation();
  const { customerCode, skuCode, vendorCode } = location.state;
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["sku-pack-type-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };

  const addPermission = allowAll || (Boolean(permissions["SKU Pack Type"]) && permissions["SKU Pack Type"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["SKU Pack Type"]) && permissions["SKU Pack Type"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["SKU Pack Type"]) && permissions["SKU Pack Type"].findIndex(b => b==="deactivate") !== -1);

  useQuery({
    queryKey: ["sku-pack-type-list", { active }],
    queryFn: () =>
      getSKUPackTypeList2({ customerCode, skuCode, vendorCode: vendorCode ?? "", active }),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["sku-pack-type-list", "status"],
    queryFn: () =>
      getSKUPackTypeStatusCount({ customerCode, skuCode, vendorCode: vendorCode ?? "" }),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateSKUPackTypeById, { onSuccess, onError });

  const activeMutation = useMutation(activeSKUPackType, { onSuccess, onError });

  const onEdit = (req) => navigate("/setup/sku/pack-type/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate SKU pack type ${req.skuCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate SKU pack type ${req.skuCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Pack Type", accessorKey: "packType", width: "100px" },
    { header: "SKU Code", accessorKey: "skuCode", width: "200px" },
    { header: "Customer Code", accessorKey: "customerCode" },
    { header: "Base UOM", accessorKey: "baseUOM", width: "100px" },
    { header: "UPM Case", accessorKey: "upmCase", width: "100px" },
    { header: "UPM Pallet", accessorKey: "upmPallet", width: "100px" },
    { header: "UPM Gross Weight", accessorKey: "upmGrossWeight" },
    { header: "UPM Cubic", accessorKey: "upmCubic", width: "100px" },
    { header: "Pallet Type", accessorKey: "palletType", width: "100px" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      width: "100px",
      cell: ({ row }) => {
        const { skuCode, customerCode, vendorCode,packType } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit sku pact type"
                disabled={!editPermission}
                onClick={() => onEdit({ skuCode, customerCode, vendorCode })}
              />
              {/* <DelButton
                title="deactivate sku pack type"
                onClick={() => onDelete({ skuCode, customerCode, vendorCode })}
              /> */}
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate sku pack type"
              disabled={!editPermission}
              onClick={() => onActive({ skuCode, customerCode, vendorCode,packType })}
            />
          );
        }
      },
    },
  ];
  const rows = data;
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/setup/sku/pack-type/add",{state:{skuCode}})}
          >
            <Icon>add</Icon>&nbsp;sku pack type
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={rows} />
      </TableCard>
    </ListLayout>
  );
}

export default SKUPackTypeList;
